import React from "react";
import { useTranslation } from "react-i18next";

import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import { Right } from "core/blueprint/base/BlueprintService";
import { mergeRights, splitRight } from "core/blueprint/base/access/Utils";

export type UserAccessRowProps = {
	user: string;
	rights: Right[];

	disabled: boolean;
	error: boolean;

	onUpdateUser: (user: string) => void;
	onUpdateRights: (rights: Right[]) => void;
};

// This component only handles the pure visualisation aspect.
// It does not have enough information itself to decide whether data is valid.
const UserAccessRow: React.FC<UserAccessRowProps> = (props: UserAccessRowProps) => {
	const { t } = useTranslation();

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "Enter") {
			const element = e.target as HTMLInputElement;
			props.onUpdateUser(element.value.toLowerCase());
		}
	}

	function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
		props.onUpdateUser(e.target.value.toLowerCase());
	}

	function updateRights(e: React.ChangeEvent<HTMLInputElement>): void {
		const rightString = e.target.value as keyof typeof Right;
		const right = Right[rightString];
		props.onUpdateRights(splitRight(right));
	}

	return (
		<TableRow>
			<TableCell>
				<TextField
					fullWidth
					defaultValue={props.user}
					disabled={props.disabled}
					error={props.error}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					variant="standard"
				/>
			</TableCell>
			<TableCell>
				<TextField
					select
					fullWidth
					value={mergeRights(props.rights) ?? ""}
					error={props.rights.length === 0}
					disabled={props.disabled}
					onChange={updateRights}
					variant="standard">
					<MenuItem key={Right.ADMIN} value={Right.ADMIN}>
						{t("admin")}
					</MenuItem>
					<MenuItem key={Right.WRITE} value={Right.WRITE}>
						{t("write")}
					</MenuItem>
					<MenuItem key={Right.READ} value={Right.READ}>
						{t("read")}
					</MenuItem>
				</TextField>
			</TableCell>
		</TableRow>
	);
};

export default UserAccessRow;
